import { deleteUploadAPI, updateUploadApi } from '../api/textures';
import { ViewInfoIcon, WarningContentInfoIcon } from '../assets/icons';
import PaymentDoneIcon from '../components/icons/PaymentDone';
import { API_MATERIAL_UPLOAD_STATUS, MODEL_TYPES, STATUS_DONE_KEY } from '../constants/constants';
import { useUserContext } from '../contexts/UserContext';
import {
  apiBulkDeleteMaterials,
  apiBulkDeleteTextures,
  apiBulkDeleteTexturesAdmin,
  apiTextureDeleteAndRestore,
  assignThisTexture,
} from '../helpers/api';
import {
  addOneMonthAddCurrentYear,
  get3DModelProductAndTextureStatus,
  getThumbnailCardTagList,
  isImageJob,
  isViewerJob,
  regenrateContentDialog,
} from '../helpers/jsHelper';
import { errorToast, multiInfoToast, successToast, warningToast } from '../helpers/toastHelper';
import { getMultipleResponseList } from '../helpers/urlHelper';
import { isAdminOrOwner } from '../helpers/userHelper';
import useCurrentOrganizationInfo from '../hooks/useCurrentOrganizationInfo';
import { useInfoDialogaActions } from '../hooks/useInfoDialogaActions';
import { useTextureActions } from '../hooks/useTextureActions';
import { uploadsPageSelectors } from '../redux/slicers/uploadsPageSlicer';
import ThumbnailCardUI from './ThumbnailCardUI';
import ThumbnailCardV2, { ACTION } from './ThumbnailCardV2';
import { textureDetailsModalActions } from '../redux/slicers/textureDetailsModalSlicer';
import { useDispatch } from 'react-redux';
import { useMemo } from 'react';
import { useStaticPermissions } from '../hooks/usePermissions';
import { PERMISSION } from '../constants/permissionConstants';
import { Mixpanel, MxEvent } from './Mixpanel';

export const ThumbnailCardTexture = ({
  data: _data,
  size,
  fullWidth,
  onSelect,
  selected,
  modelType,
  onDelete,
  onEdit,
  hideFavourite,
  customModelActionsConfig,
  onSuccess,
  designV2,
  showMenu,
  isMaterialList,
  isTexture,
  ...rest
}) => {
  const data = useMemo(() => {
    if (isMaterialList && rest?.originalData) {
      return {
        ..._data,
        member_details: {
          ..._data?.created_by_details,
          id: +_data?.created_by_details?.user_id,
          member_id: _data?.created_by_details?.id,
          name: `${_data?.created_by_details?.first_name}${
            _data?.created_by_details?.last_name ? ` ${_data?.created_by_details?.last_name}` : ''
          }`,
        },
      };
    }
    return _data;
  }, [isMaterialList, rest.originalData, _data]);

  const { isUnlimitedTextures, isAllowExpiredTextures } = useCurrentOrganizationInfo();
  const { currentUser, isEditor, isGuestUser } = useUserContext();
  const {
    onDelete: _onDelete,
    onFavouriteToggle,
    favouriteLoading,
    onShare,
    addToCollection,

    loading,
    handleDownloadClick,
    onEdit: _onEdit,
    handleUploadEditClick,
    handleCardClick,
  } = useTextureActions({
    item: data,
    modelType,
    customModelActionsConfig,
    isMaterialList,
  });

  const { openInfoDialog, closeInfoDialog } = useInfoDialogaActions();
  const dispatch = useDispatch();
  const { hasPermission } = useStaticPermissions();

  const canAccessV2Page = isGuestUser() || hasPermission(PERMISSION.PRODUCT_PAGE_V2);
  const canAssignTexture = hasPermission(PERMISSION.CAN_ASSIGN_TEXTURE);
  const canDownloadTexture = hasPermission(PERMISSION.DOWNLOAD_TEXTURE) && isTexture;
  const isMattressTexture = data?.is_mattress_texture;

  const productJobStatus = canAccessV2Page
    ? get3DModelProductAndTextureStatus({
        data,
      })
    : null;

  let _tagList = useMemo(
    () =>
      canAccessV2Page
        ? getThumbnailCardTagList(data, canAccessV2Page)
        : isMattressTexture
        ? [{ type: 'pending', label: 'Exclusive for Mattress' }]
        : [],
    [data, canAccessV2Page]
  );

  if (isMaterialList) {
    if (data.material_job_status === 0 || data.material_job_status === 2) {
      _tagList = [{ type: 'pending', label: 'In progress' }];
    } else if (data.material_job_status === 3) {
      _tagList = [{ type: 'failed', label: 'Failed' }];
    }
  }

  const _hideFavourite = hideFavourite || productJobStatus;

  const isUpload = modelType === MODEL_TYPES.UPLOADS;
  // const handleDelete = onDelete || _onDelete;
  let handleEdit = !isMaterialList && (onEdit || _onEdit);

  const showToast = (source, target = data) => {
    if (!source || !target) return;
    multiInfoToast(getMultipleResponseList(source, [target], 'Deleted'), `Delete Details`);
  };

  const assignTexture = async () => {
    await assignThisTexture([data.id]);
  };

  const hideEditInfoOption =
    isMaterialList && !isEditor && +rest?.originalData?.created_by_details?.id !== +currentUser?.id;
  if (hideEditInfoOption) {
    rest.customActions = {
      [ACTION.INFO]: {
        label: 'Edit Info',
        icon: ViewInfoIcon,
        onClick: null,
      },
    };
  }
  if (rest?.isCollectionItem && isMaterialList) {
    rest.customActions = {
      ...rest.customActions,
      [ACTION.INFO]: {
        label: 'Edit Info',
        icon: ViewInfoIcon,
        onClick: null,
      },
      [ACTION.ADD_TO_COLLECTION]: {
        label: 'Edit Info',
        onClick: null,
      },
      [ACTION.DOWNLOAD]: {
        label: 'Edit Info',
        onClick: null,
      },
    };
  }

  const handleDeleteGuestUser = (id) => {
    openInfoDialog({
      topIcon: <WarningContentInfoIcon />,
      title: 'Are you sure you want to delete this texture?',
      description: (
        <p>The selected texture may be linked to certain items(projects, props, renders, etc)</p>
      ),
      titleStyle: { textAlign: 'start' },
      cancelButtonText: 'Cancel',
      buttonText: 'Confirm',
      size: 'lg',
      onButtonClick: async () => {
        const formData = new FormData();
        formData.append('id', id);
        await apiBulkDeleteTexturesAdmin(formData)
          .then((res) => {
            if (res?.status) {
              if (rest?.refetchList) {
                rest?.refetchList?.();
              }
              successToast(res?.message || 'Your texture deleted successfully');
              if (onSuccess) {
                onSuccess?.();
              }
            } else {
              errorToast(res?.message || 'Something went wrong. Please try again.');
            }
          })
          .catch((error) => {
            showToast(error.data);
          });
      },
    });
  };

  const handleDelete = (id) => {
    if (!isAdminOrOwner(currentUser) && data?.member_details?.member_id !== currentUser.id) {
      warningToast('You do not have ownership to delete this item.', {
        toastId: `CAN_NOT_DELETE_${id}`,
      });
      return;
    }
    if (isMaterialList) {
      openInfoDialog({
        title: 'Are you sure you want to delete this material?',
        description: 'Your items can be restored from the Trash area.',
        cancelButtonText: 'Cancel',
        buttonText: 'Delete',
        onButtonClick: async () => {
          const formData = new FormData();
          formData.append('id', id);

          await apiBulkDeleteMaterials(formData)
            .then((res) => {
              showToast(res.data);
              if (rest?.refetchList) {
                closeInfoDialog();
                rest?.refetchList?.();
              }
              if (onSuccess) {
                onSuccess?.();
              }
            })
            .catch((error) => {
              showToast(error.data);
            });
        },
      });
    }
    // const date = data?.delete_expired_date && addOneMonthAddCurrentYear(data?.delete_expired_date);
    const date = null;

    const handleSuccessMessage = () => {
      openInfoDialog({
        topIcon: <PaymentDoneIcon />,
        title: 'Your delete request has been submitted.',
        description: (
          <>
            {date ? (
              <>
                {`All the items (projects, props, renders, etc.) using this texture will no longer be accessible after`}{' '}
                <strong>
                  <b>{date}</b>
                </strong>
                .
              </>
            ) : (
              <>
                All items (projects, props, renders, etc.) using these texture will no longer be
                accessible after their respective deletion dates.
              </>
            )}
          </>
        ),
        titleStyle: { textAlign: 'start' },
        buttonText: 'Okay',
        size: 'lg',
        onButtonClick: () => closeInfoDialog(),
      });
    };
    if (!data?.is_special_case_texture && !isUnlimitedTextures && !isMaterialList) {
      openInfoDialog({
        topIcon: <WarningContentInfoIcon />,
        title: 'Are you sure you want to delete this texture?',
        description: (
          <>
            {isExpired ? (
              <p>
                {`You’ve selected inactive Texture. Once confirmed, these texture will be deleted
                permanently. This action cannot be undone.`}
              </p>
            ) : date ? (
              <>
                {`This texture maybe linked to certain items (projects, props, renders, etc.). You won’t be able to access these items after`}{' '}
                <strong>
                  <b>{date}</b>
                </strong>
                .
              </>
            ) : (
              <>
                <p>
                  The selected texture may be linked to certain items(projects, props, renders,
                  etc). After the respective subscription period ends, you will no longer have
                  access to these linked items.
                </p>

                <p>
                  To retain access, you can restore this texture before the respective subscription
                  period ends.
                </p>
              </>
            )}
          </>
        ),
        secondLineDescription:
          isExpired || !date
            ? ''
            : `You can restore the texture before this date to retain access.`,
        titleStyle: { textAlign: 'start' },
        cancelButtonText: 'Cancel',
        buttonText: 'Confirm',
        size: 'lg',
        onButtonClick: async () => {
          const formData = new FormData();
          formData.append('is_delete_on_expire', true);
          formData.append('texture_ids', id);
          await apiTextureDeleteAndRestore(formData)
            .then((res) => {
              if (rest?.refetchList) {
                rest?.refetchList?.();
              }
              handleSuccessMessage();
              if (onSuccess) {
                onSuccess?.();
              }
            })
            .catch((error) => {
              showToast(error.data);
            });
        },
      });
    } else {
      if (!isMaterialList) {
        openInfoDialog({
          title: 'Are you sure you want to delete this texture?',
          description: 'Your items can be restored from the Trash area.',
          cancelButtonText: 'Cancel',
          buttonText: 'Delete',
          onButtonClick: async () => {
            const formData = new FormData();
            formData.append('id', id);
            await apiBulkDeleteTextures(formData)
              .then((res) => {
                showToast(res.data);
                if (rest?.refetchList) {
                  rest?.refetchList?.();
                }
                if (onSuccess) {
                  onSuccess?.();
                }
              })
              .catch((error) => {
                showToast(error.data);
              });
          },
        });
      } else {
        if (!isMaterialList) {
          openInfoDialog({
            title: 'Are you sure you want to delete this texture?',
            description: 'Your item can be restored from the Trash area.',
            cancelButtonText: 'Cancel',
            buttonText: 'Delete',
            onButtonClick: async () => {
              const formData = new FormData();
              formData.append('id', id);

              await apiBulkDeleteTextures(formData)
                .then((res) => {
                  showToast(res.data);
                  if (rest?.refetchList) {
                    rest?.refetchList?.();
                  }
                  if (onSuccess) {
                    onSuccess?.();
                  }
                })
                .catch((error) => {
                  showToast(error.data);
                });
            },
          });
        }
      }
    }
  };

  // const belongsToOrganization = data.owner_organization === currentUser.organization;
  // const isFileOwner = data.created_by === currentUser.id;
  // const canEdit = belongsToOrganization && (isFileOwner || isAdminOrOwner(currentUser))

  if (isUpload && !isMattressTexture) {
    // || !canEdit) {
    handleEdit = undefined;
  }

  const handleRestore = () => {
    if (!data?.id) return;
    openInfoDialog({
      topIcon: <WarningContentInfoIcon />,
      title: 'Are you sure you want to restore this texture?',
      description: `This texture maybe linked to certain items (projects, props, renders etc.). Restoring it will allow you to use these items without any restrictions.`,
      cancelButtonText: 'Cancel',
      buttonText: 'Confirm',
      size: 'lg',
      titleStyle: { textAlign: 'start' },
      onButtonClick: async () => {
        const formData = new FormData();
        formData.append('is_delete_on_expire', false);
        formData.append('texture_ids', data?.id);
        const res = await apiTextureDeleteAndRestore(
          formData,
          'Your texture restored successfully'
        );
        if (res?.status) {
          rest?.refetchList?.();
        }
      },
      okBtnColor: null,
      cancelBtnColor: null,
      okBtnClassName: null,
    });
  };

  const isDeletedRequest = data?.is_delete_on_expire;
  const isSpecialCaseTexture = data?.is_special_case_texture;
  const isMattressTextureExpired = data?.is_mattress_expired;
  const isCustomerProductExpired = data?.is_customer_product_expired;
  const isExpired = isMattressTexture ? isMattressTextureExpired : isCustomerProductExpired;
  const isExpiredItem = (isUnlimitedTextures || isAllowExpiredTextures)
    ? false
    : (isSpecialCaseTexture && data?.is_archived) || (!isSpecialCaseTexture && isExpired);
  const isSameOrg = currentUser?.organization === data?.owner_organization;
  //const isExpiredItem = data?.is_customer_product_expired

  const onEditInfo = (data) => {
    if (isMaterialList && !hideEditInfoOption) {
      dispatch(textureDetailsModalActions.showMaterialInfo(data));
    } else {
      !isMaterialList && dispatch(textureDetailsModalActions.show(data));
    }
  };

  const isContentGenerated = useMemo(
    () =>
      [
        data?.generate_ar_content_status,
        data?.generate_360_content_status,
        data?.generate_images_content_status,
      ].includes(STATUS_DONE_KEY) && data?.has_content,
    [data]
  );

  const isV2 = () =>
    canAccessV2Page && (!data?.product_page_version || data?.product_page_version === '2');

  if (
    isV2() &&
    isContentGenerated &&
    !_tagList?.find(({ label }) => label?.toLowerCase() === 'generating visuals') &&
    !_tagList?.find(({ label }) => label?.toLowerCase() === 'view visuals')
  ) {
    _tagList.push({
      type: 'pending',
      label: 'View visuals',
    });
  }

  return designV2 ? (
    <ThumbnailCardV2
      id={data?.id}
      onFavouriteChange={onFavouriteToggle(data)}
      onSelect={onSelect}
      selected={selected}
      isFavourite={data.is_favourite}
      hideFavourite={_hideFavourite}
      favouriteLoading={favouriteLoading}
      titleProps={
        productJobStatus || hideEditInfoOption
          ? null
          : {
              onClick: () => (isMaterialList ? onEditInfo(data) : handleCardClick(data)),
            }
      }
      openDetails={() => (isMaterialList ? onEditInfo(data) : handleCardClick(data))}
      data={data}
      name={data?.name}
      thumbnail={data?.thumbnail}
      date={data.updated_at}
      userCard
      showMenu={showMenu && (!isGuestUser() || isEditor) && !productJobStatus}
      savePreviousRoute
      showCheckbox
      showCheckboxGuestUser={isGuestUser()}
      isFavouriteIcon
      {...(!isUpload // || !isGuestUser()
        ? {
            onEditInfo:
              !isGuestUser() && (canAccessV2Page || isMaterialList) ? () => onEditInfo(data) : null,
            onGenerateContent:
              canAccessV2Page &&
              !isMaterialList &&
              (!isGuestUser() || isEditor) &&
              (() =>
                onTextureGenerateContent(
                  data,
                  rest?.setShowApplyTextureModal,
                  openInfoDialog,
                  closeInfoDialog
                )),
          }
        : {})}
      {...(isUpload && isMattressTexture
        ? {
            onEditInfo: !isGuestUser() && canAccessV2Page ? () => onEditInfo(data) : null,
          }
        : {})}
      {...(!isUpload || (isUpload && isMattressTexture)
        ? isGuestUser() || isMaterialList
          ? {
              onEdit: undefined,
            }
          : {
              onEdit: (e, data, originalData) => {
                handleEdit(e, data, originalData);
                window.$previousURL = `${window.location.pathname}${window.location.search}`;
                window.$restrictFilter = true;
                window.$scrollPosition = window.scrollY;
              },
            }
        : {
            onEdit: handleEdit,
          })}
      onAssignTexture={canAssignTexture ? assignTexture : null}
      onMoveToTextures={!isGuestUser() && isUpload ? handleUploadEditClick : undefined}
      onDelete={isGuestUser() ? (isSameOrg ? handleDeleteGuestUser : null) : handleDelete}
      onRestore={!isGuestUser() && isDeletedRequest ? handleRestore : null}
      onShare={!isGuestUser() && !isUpload && !isMaterialList ? onShare : undefined}
      onDownload={canDownloadTexture ? handleDownloadClick : null}
      downloadLoading={loading}
      onAddToCollection={!isGuestUser() && !isUpload ? addToCollection : undefined}
      rotation={data?.rotation}
      blurCard={(isDeletedRequest || isExpiredItem) && 'blurCard'}
      tagList={
        isDeletedRequest || isExpiredItem
          ? [{ type: 'deleteRequest', label: isDeletedRequest ? 'Deletion requested' : 'Expired' }]
          : _tagList
      }
      tagListStyle={
        (typeof productJobStatus === 'string' &&
          productJobStatus?.toLowerCase() === 'generating visuals') ||
        isMaterialList
          ? {
              right: 0,
              bottom: 0,
            }
          : {}
      }
      tagListItemStyle={
        typeof productJobStatus === 'string' &&
        productJobStatus?.toLowerCase() === 'generating visuals'
          ? { textTransform: 'uppercase' }
          : {}
      }
      dateLabel={
        isDeletedRequest && data?.delete_expired_date
          ? `Permanently delete on ${addOneMonthAddCurrentYear(data?.delete_expired_date)}`
          : ''
      }
      {...rest}
    />
  ) : (
    <ThumbnailCardUI
      key={data.id}
      id={data.id}
      onEdit={handleEdit}
      onMoveToTextures={isUpload ? handleUploadEditClick : undefined}
      thumbnail={data?.thumbnail}
      images={data?.images}
      name={data.name}
      isFavourite={data.is_favourite}
      favouriteLoading={favouriteLoading}
      date={data.updated_at}
      onDelete={
        isGuestUser()
          ? !isDeletedRequest && isSameOrg
            ? handleDeleteGuestUser
            : null
          : handleDelete
      }
      onShare={!isUpload ? onShare : undefined}
      onFavouriteChange={onFavouriteToggle(data)}
      size={size}
      data={data}
      titleProps={
        productJobStatus
          ? null
          : {
              onClick: () => handleCardClick(data),
            }
      }
      {...(!isUpload // || !isGuestUser()
        ? {
            onEditInfo:
              !isGuestUser() && (canAccessV2Page || isMaterialList) ? () => onEditInfo(data) : null,
            onGenerateContent:
              canAccessV2Page &&
              (!isGuestUser() || isEditor) &&
              (() =>
                onTextureGenerateContent(
                  data,
                  rest?.setShowApplyTextureModal,
                  openInfoDialog,
                  closeInfoDialog
                )),
          }
        : {})}
      {...(isUpload && isMattressTexture
        ? {
            onEditInfo: !isGuestUser() && canAccessV2Page ? () => onEditInfo(data) : null,
          }
        : {})}
      // onDownload={handleDownloadClick}
      tagList={
        isDeletedRequest || isExpiredItem
          ? [{ type: 'deleteRequest', label: isDeletedRequest ? 'Deletion requested' : 'Expired' }]
          : _tagList
      }
      tagListStyle={
        (typeof productJobStatus === 'string' &&
          productJobStatus?.toLowerCase() === 'generating visuals') ||
        isMaterialList
          ? {
              right: 0,
              bottom: 0,
            }
          : {}
      }
      tagListItemStyle={
        typeof productJobStatus === 'string' &&
        productJobStatus?.toLowerCase() === 'generating visuals'
          ? { textTransform: 'uppercase' }
          : {}
      }
      downloadLoading={loading}
      fullWidth={fullWidth}
      onSelect={onSelect}
      selected={selected}
      onAddToCollection={!isUpload ? addToCollection : undefined}
      onCardImageClick={() => handleCardClick(data)}
      hideFavourite={_hideFavourite}
      rotation={data?.rotation}
      {...rest}
    />
  );
};

export const ThumbnailCardUpload = (props) => {
  const customModelActionsConfig = {
    modelType: MODEL_TYPES.TEXTURE,
    selectors: uploadsPageSelectors,
    updateApi: updateUploadApi,
    deleteConfig: {
      title: 'Are you sure you want to delete this custom map?',
      deleteApi: deleteUploadAPI,
    },
  };

  return (
    <ThumbnailCardTexture
      {...props}
      modelType={MODEL_TYPES.UPLOADS}
      customModelActionsConfig={customModelActionsConfig}
    />
  );
};

export const onTextureGenerateContent = (
  data,
  setShowApplyTextureModal,
  openInfoDialog,
  closeInfoDialog
) => {
  function generateContent(_data) {
    if (setShowApplyTextureModal) {
      const _isViewerJob = isViewerJob(_data);
      const _isImageJob = isImageJob(_data);
      setShowApplyTextureModal({ ...data, isViewerJob: _isViewerJob, isImageJob: _isImageJob });
    }
  }
  Mixpanel.track(true, MxEvent.CLICK_GENERATE_VISUAL);
  if (data?.has_content) {
    regenrateContentDialog({ generateContent, openInfoDialog, closeInfoDialog, data });
  } else {
    generateContent(data);
  }
};
